import { useEffect, useMemo, useState } from "react";
import "../modules/align/alignParameterSelector/AlignPopupParameterSelector.css";
import "./ComponentPlayground.css";

import Cage from "../modules/align/alignParameterSelector/Cage";
import GlobalAlignment from "../modules/align/alignParameterSelector/GlobalAlignment";
import Osteotomy from "../modules/align/alignParameterSelector/Osteotomy";
import Rod from "../modules/align/alignParameterSelector/Rod";
import SegmentalTransformation from "../modules/align/alignParameterSelector/SegmentalTtransformation";
import { useNotification } from "../classes/contexts/NotificationContext";
import { SnackbarType } from "../classes/enums/SnackbarTypeEnum";

const PREVIEW_OPTIONS = [
  { key: "notifications", label: "Notifications" },
  { key: "global", label: "Global Alignment" },
  { key: "rod", label: "Rod" },
  { key: "cage", label: "Cage" },
  { key: "osteotomy", label: "Osteotomy" },
  { key: "segmental", label: "Segmental Transformation" },
];

const createRod = index => ({
  id: `rod-${index}`,
  rodPairId: `pair-${index}`,
  rodDiameter: 6,
  overhangLength: 2,
  selectedLeftScrews: ["L2", "L3"],
  selectedRightScrews: ["L2", "L3"],
  rodType: "Standard",
  rodManufacturer: "MDsim",
  rodMaterial: "Titanium",
  screwManufacturer: "MDsim",
  screwProduct: "Default",
  screwMaterial: "Titanium",
  screwDiameter: 6.5,
  screwLength: 45,
  range: ["L2", "L4"],
});

const NotificationsTester = () => {
  const { addNotification, clearAllNotifications } = useNotification();
  const [count, setCount] = useState(5);

  const addMultipleNotifications = (type, persistent) => {
    for (let i = 1; i <= count; i++) {
      addNotification(type, `Test notification message #${i}`, {
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} ${i}`,
        duration: persistent ? 0 : 4000,
      });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "20px" }}>
      <h3>Notification Stack Tester</h3>

      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <label>Number of notifications:</label>
        <input
          type="number"
          min="1"
          max="20"
          value={count}
          onChange={e => setCount(Number(e.target.value))}
          style={{ width: "60px", padding: "4px" }}
        />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        <button onClick={() => addMultipleNotifications(SnackbarType.ERROR, true)}>
          Add {count} Persistent Errors
        </button>
        <button onClick={() => addMultipleNotifications(SnackbarType.ERROR, false)}>
          Add {count} Timed Errors
        </button>
        <button onClick={() => addMultipleNotifications(SnackbarType.WARNING, true)}>
          Add {count} Persistent Warnings
        </button>
        <button onClick={() => addMultipleNotifications(SnackbarType.SUCCESS, false)}>
          Add {count} Success
        </button>
        <button onClick={() => addMultipleNotifications(SnackbarType.INFO, false)}>
          Add {count} Info
        </button>
      </div>

      <button
        onClick={clearAllNotifications}
        style={{ backgroundColor: "#dc3545", color: "white", padding: "8px 16px" }}
      >
        Clear All Notifications
      </button>

      <p style={{ color: "#666", fontSize: "14px" }}>
        Test with many persistent notifications to check scrolling behavior.
      </p>
    </div>
  );
};

const ComponentPlayground = () => {
  const [selectedPreview, setSelectedPreview] = useState(
    PREVIEW_OPTIONS[0].key
  );
  const [rods, setRods] = useState(() => [createRod(1)]);

  useEffect(() => {
    document.title = "UI playground";
  }, []);

  const vertebraOptions = useMemo(() => ["L2", "L3", "L4", "L5", "S1"], []);

  const dropdownItems = useMemo(
    () => [
      { value: "-", label: "-" },
      ...vertebraOptions.map(level => ({ value: level, label: level })),
    ],
    [vertebraOptions]
  );

  const screwSpecs = useMemo(() => {
    const specs = {};
    vertebraOptions.forEach(level => {
      specs[level] = { diameter: 6.5, length: 45 };
    });
    return specs;
  }, [vertebraOptions]);

  const updateRod = (rodId, updater) => {
    setRods(prev =>
      prev.map(rod => {
        if (rod.id !== rodId) return rod;
        const patch =
          typeof updater === "function" ? updater(rod) : { ...updater };
        return { ...rod, ...patch };
      })
    );
  };

  const addRod = () => {
    setRods(prev => [...prev, createRod(prev.length + 1)]);
  };

  const removeRod = rodId => {
    setRods(prev => prev.filter(rod => rod.id !== rodId));
  };

  const normalizeValue = item => {
    if (!item) return "-";
    if (item.target && item.target.value !== undefined)
      return item.target.value;
    if (item.value !== undefined) return item.value;
    return "-";
  };

  const vertebraRangeSelection = {
    handleFirstVertebraChange: (rod, item) => {
      const value = normalizeValue(item);
      updateRod(rod.id, {
        range: value === "-" ? [] : [value, rod.range?.[1] ?? value],
      });
    },
    handleSecondVertebraChange: (rod, item) => {
      const value = normalizeValue(item);
      updateRod(rod.id, {
        range:
          value === "-"
            ? rod.range?.slice(0, 1) ?? []
            : [rod.range?.[0] ?? value, value],
      });
    },
    getRangeForRod: rod => rod.range ?? [],
    getSelectedVertebraItem: rod => {
      const first = rod.range?.[0] ?? "-";
      return (
        dropdownItems.find(option => option.value === first) || dropdownItems[0]
      );
    },
    getLastSelectedVertebraItem: rod => {
      const last =
        rod.range && rod.range.length > 1
          ? rod.range[rod.range.length - 1]
          : rod.range?.[0] ?? "-";
      return (
        dropdownItems.find(option => option.value === last) || dropdownItems[0]
      );
    },
    getFirstDropdownItems: () => dropdownItems,
    getSecondDropdownItems: () => dropdownItems,
    getSideAvailability: () => ({ leftAvailable: true, rightAvailable: true }),
  };

  const renderPreview = previewKey => {
    switch (previewKey) {
      case "notifications":
        return <NotificationsTester />;

      case "global":
        return (
          <GlobalAlignment
            preOpGlobalAlignment={{
              pelvicTilt: 10,
              kneeAngle: 5,
              method: null,
            }}
          />
        );

      case "rod":
        return (
          <Rod
            rods={rods}
            screwSpecs={screwSpecs}
            vertebraRangeSelection={vertebraRangeSelection}
            onAddRod={addRod}
            onUpdateRod={updateRod}
            onRemoveRod={removeRod}
            onConfirm={() => {}}
          />
        );

      case "cage":
        return <Cage />;

      case "osteotomy":
        return <Osteotomy />;

      case "segmental":
      default:
        return <SegmentalTransformation />;
    }
  };

  return (
    <div className="component-playground-plain">
      <div className="align-preview-controls">
        <label className="App-text-medium" htmlFor="align-preview-select">
          Select panel
        </label>
        <select
          id="align-preview-select"
          value={selectedPreview}
          onChange={event => setSelectedPreview(event.target.value)}
        >
          {PREVIEW_OPTIONS.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="align-preview-shell">
        {renderPreview(selectedPreview)}
      </div>
    </div>
  );
};

export default ComponentPlayground;
